import React, { useState } from "react";
import { toast } from "sonner";
import { Barcode, Copy, Download, RefreshCw, Loader2 } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUS = { aberto: ["Aguardando pagamento", "bg-amber-100 text-amber-800"], pago: ["Pago", "bg-emerald-100 text-emerald-800"], baixado: ["Baixado", "bg-slate-200 text-slate-700"] };

const copy = (t, label) => navigator.clipboard?.writeText(t).then(() => toast.success(`${label} copiado`));

export const downloadBoleto = async (budgetId, number) => {
  try {
    const r = await api.get(`/budgets/${budgetId}/boleto/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = url; a.download = `boleto_orc${number || ""}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Não foi possível baixar o boleto"); }
};

function RetryBoleto({ budgetId, onDone }) {
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/budgets/${budgetId}/boleto`, { due_date: due }); toast.success("Boleto gerado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao gerar boleto", { duration: 9000 }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2" data-testid="boleto-retry">
      <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9" data-testid="boleto-retry-due" />
      <Button size="sm" onClick={go} disabled={!due || busy} data-testid="boleto-retry-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Gerar boleto"}</Button>
    </div>
  );
}

export default function BoletoBox({ budgetId, number, boleto, onChanged }) {
  const [checking, setChecking] = useState(false);
  const check = async () => {
    setChecking(true);
    try { const r = await api.post(`/budgets/${budgetId}/boleto/check`); toast.success(r.data.status === "pago" ? "Boleto PAGO!" : "Ainda não consta pagamento"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao consultar o banco"); }
    finally { setChecking(false); }
  };
  if (!boleto?.numero || boleto.status === "baixado") {
    return (
      <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="boleto-box-empty">
        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><Barcode className="w-4 h-4" /> {boleto?.status === "baixado" ? "Boleto baixado — gerar outro?" : "Gerar boleto para esta nota"}</p>
        <RetryBoleto budgetId={budgetId} onDone={onChanged} />
      </div>
    );
  }
  const [label, cls] = STATUS[boleto.status] || STATUS.aberto;
  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid="boleto-box">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-bold text-slate-700 flex items-center gap-1.5"><Barcode className="w-4 h-4" /> Boleto {boleto.ambiente !== "producao" && "(teste)"}</p>
        <span className={`text-[10.5px] font-bold rounded-full px-2 py-0.5 ${cls}`} data-testid="boleto-status">{label}{boleto.status === "pago" && boleto.pago_em ? ` em ${boleto.pago_em}` : ""}</span>
      </div>
      <p className="text-[11px] text-slate-600" data-testid="boleto-values">{BRL(boleto.valor_total)} − ISS {BRL(boleto.iss_retido)} = <b>{BRL(boleto.valor)}</b> · vence {formatDateBR(boleto.vencimento)}</p>
      {boleto.linha_digitavel && (
        <button type="button" onClick={() => copy(boleto.linha_digitavel, "Código de barras")} className="w-full text-left text-[10.5px] font-mono bg-slate-50 border rounded p-1.5 break-all flex gap-1.5" data-testid="boleto-copy-linha">
          <Copy className="w-3.5 h-3.5 shrink-0" />{boleto.linha_digitavel}
        </button>
      )}
      <div className="grid grid-cols-3 gap-1.5">
        <Button size="sm" variant="outline" onClick={() => downloadBoleto(budgetId, number)} data-testid="boleto-download"><Download className="w-3.5 h-3.5 mr-1" />PDF</Button>
        <Button size="sm" variant="outline" disabled={!boleto.pix_emv} onClick={() => copy(boleto.pix_emv, "Pix copia e cola")} data-testid="boleto-copy-pix"><Copy className="w-3.5 h-3.5 mr-1" />Pix</Button>
        <Button size="sm" variant="outline" disabled={checking || boleto.status === "pago"} onClick={check} data-testid="boleto-check">
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Conferir
        </Button>
      </div>
      {boleto.status === "aberto" && <p className="text-[10.5px] text-slate-400">O app confere o pagamento sozinho a cada hora e avisa você.</p>}
    </div>
  );
}
